import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import SpecialRequestDetailModal from './SpecialRequestDetailModal';
import { Eye, Calendar, DollarSign, Users, Clock, AlertTriangle, Trash2, Search, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

interface SpecialRequest {
  id: string;
  full_name: string | null;
  email: string;
  phone: string | null;
  materials_needed: string;
  quantities: string | null;
  budget: number | null;
  deadline: string | null;
  message: string | null;
  status: string;
  admin_response: string | null;
  created_at: string;
  user_id: string | null;
}

const SpecialRequestsModule = () => {
  const [requests, setRequests] = useState<SpecialRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<SpecialRequest | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      // Use full access RPC function for admin
      const { data, error } = await supabase
        .rpc('get_admin_special_requests_full_access');

      if (error) throw error;

      setRequests(data || []);
    } catch (error) {
      console.error('Error fetching special requests:', error);
      toast.error('שגיאה בטעינת בקשות מיוחדות');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'responded':
        return 'default';
      case 'completed':
        return 'default';
      case 'rejected':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'ממתין';
      case 'responded':
        return 'נענה';
      case 'completed':
        return 'הושלם';
      case 'rejected':
        return 'נדחה';
      default:
        return status;
    }
  };

  const getPendingRequests = () => requests.filter(r => r.status === 'pending').length;
  const getRespondedRequests = () => requests.filter(r => r.status === 'responded').length;
  const getUrgentRequests = () => {
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return requests.filter(r => r.deadline && new Date(r.deadline) <= weekFromNow).length;
  };

  const handleViewRequest = (request: SpecialRequest) => {
    setSelectedRequest(request);
    setIsDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedRequest(null);
    setIsDetailModalOpen(false);
  };

  const handleRequestUpdated = () => {
    fetchRequests();
  };

  const handleDeleteRequest = async (requestId: string) => {
    try {
      const { error } = await supabase.rpc('delete_special_request_cascade', {
        request_id_param: requestId
      });

      if (error) throw error;

      toast.success('בקשה מיוחדת נמחקה בהצלחה');
      fetchRequests();
    } catch (error: any) {
      console.error('Error deleting special request:', error);
      toast.error(`שגיאה במחיקת הבקשה: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">טוען בקשות מיוחדות...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">סה"כ בקשות</CardTitle>
            <Search className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{requests.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">ממתינות למענה</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{getPendingRequests()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">נענו</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{getRespondedRequests()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">דחופות</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{getUrgentRequests()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            בקשות חיפוש מוצרים מיוחדים
          </CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-12">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">אין בקשות מיוחדות עדיין</h3>
              <p className="text-muted-foreground">
                בקשות חיפוש מוצרים מיוחדים יופיעו כאן
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-right whitespace-nowrap">שם מלא</TableHead>
                      <TableHead className="text-right whitespace-nowrap">אימייל</TableHead>
                      <TableHead className="text-right whitespace-nowrap">חומרים נדרשים</TableHead>
                      <TableHead className="text-right whitespace-nowrap">תקציב</TableHead>
                      <TableHead className="text-right whitespace-nowrap">מועד יעד</TableHead>
                      <TableHead className="text-right whitespace-nowrap">סטטוס</TableHead>
                      <TableHead className="text-right whitespace-nowrap">תאריך</TableHead>
                      <TableHead className="text-right whitespace-nowrap">פעולות</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          <div className="font-medium">{request.full_name || 'לא צוין'}</div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{request.email}</div>
                            {request.phone && (
                              <div className="text-sm text-muted-foreground">{request.phone}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-48">
                          <div className="truncate font-medium">
                            {request.materials_needed}
                          </div>
                          {request.quantities && (
                            <div className="text-sm text-muted-foreground truncate">
                              כמויות: {request.quantities}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {request.budget ? (
                            <div className="font-medium">₪{request.budget.toLocaleString()}</div>
                          ) : (
                            <span className="text-muted-foreground">לא הוגדר</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {request.deadline ? (
                            <div className={`font-medium ${new Date(request.deadline) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                                ? 'text-red-600'
                                : 'text-foreground'
                              }`}>
                              {new Date(request.deadline).toLocaleDateString('he-IL')}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">גמיש</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(request.status)}>
                            {getStatusText(request.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(request.created_at).toLocaleDateString('he-IL')}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewRequest(request)}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              צפייה
                            </Button>

                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="destructive" size="sm">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  מחק
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent dir="rtl">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>האם אתה בטוח?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    פעולה זו תמחק את הבקשה המיוחדת לצמיתות. לא ניתן לבטל פעולה זו.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>ביטול</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteRequest(request.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    מחק
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Special Request Detail Modal */}
      <SpecialRequestDetailModal
        request={selectedRequest}
        isOpen={isDetailModalOpen}
        onClose={handleCloseModal}
        onRequestUpdated={handleRequestUpdated}
      />
    </div>
  );
};

export default SpecialRequestsModule;